import { DownloadCategory } from './types';

// Gap between consecutive download triggers. Browsers can treat a
// burst of back-to-back downloads as spam and drop all but the first,
// so each file is handed over on its own after a short pause.
export const DOWNLOAD_TRIGGER_INTERVAL_MS = 500;

export type DownloadSignedUrlFile = {
  name: string;
  path: string;
  url: string;
  // Optional: an older backend omits it and the download stays flat.
  folder_path?: string;
};
export type DownloadSignedUrlsResponse = {
  files: DownloadSignedUrlFile[];
};
// 202 from POST /api/download: the backend is building one zip of the
// selection; poll GET /api/download/jobs/{job_id} until it answers 200
// with a DownloadSignedUrlsResponse holding that single zip.
export type DownloadJobPreparingResponse = {
  status: 'preparing';
  job_id: string;
};

export type DownloadRequestMeta = {
  category: DownloadCategory;
  game_ids?: number[];
  file_ids?: number[];
  market_ids?: number[];
  partner_ids?: number[];
  entity_name?: string;
  game_name?: string;
};

export type DownloadParams = DownloadRequestMeta & {
  extension?: string;
};

export type DownloadProgressCallback = (
  completed: number,
  total: number,
) => void;

const DOWNLOAD_CATEGORY_LABELS: Record<DownloadCategory, string> = {
  game_sheets: 'Game Sheets',
  compliance_documents: 'Compliance Documents',
  marketing_assets: 'Marketing Assets',
  all: '',
};

// Builds the "game - asset type" label shown for a batch in the
// download manager, e.g. "Mr Coin - Marketing Assets".
export const buildDownloadTitle = (meta: DownloadRequestMeta) => {
  const name = meta.game_name || meta.entity_name || '';
  const categoryLabel = DOWNLOAD_CATEGORY_LABELS[meta.category];
  return (
    [name, categoryLabel].filter(Boolean).join(' - ') || 'Download'
  );
};

const getFilenameFromUrl = (url: string) => {
  const lastSegment = url.split('/').pop() || '';
  const withoutQuery = lastSegment.split('?')[0] || '';
  try {
    return decodeURIComponent(withoutQuery);
  } catch {
    return withoutQuery;
  }
};

const resolveFileName = (
  file: DownloadSignedUrlFile,
  index: number,
) => {
  const rawName =
    typeof file.name === 'string' ? file.name.trim() : '';
  const pathLeaf =
    typeof file.path === 'string'
      ? (file.path.split('/').pop() || '').trim()
      : '';
  return (
    rawName ||
    pathLeaf ||
    getFilenameFromUrl(file.url) ||
    `file-${index + 1}`
  );
};

// Hands one file to the browser as a plain link click, exactly like a
// user clicking a download link: the browser streams it to disk and
// applies its own "ask where to save" setting. The signed URLs are
// cross-origin, so `download` is ignored there and the saved name
// comes from the storage's Content-Disposition header.
const triggerNativeDownload = (url: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Triggers one native browser download per signed-url file, one after
// another. There is no zip and no File System Access API: the browser
// owns each transfer, so failures can't be observed from here and the
// returned list of failed files is always empty.
export const saveDownloadedFiles = async (
  files: DownloadSignedUrlFile[],
  _meta: DownloadRequestMeta,
  onProgress?: DownloadProgressCallback,
): Promise<DownloadSignedUrlFile[]> => {
  for (const [index, file] of files.entries()) {
    if (index > 0) {
      await new Promise((resolve) =>
        setTimeout(resolve, DOWNLOAD_TRIGGER_INTERVAL_MS),
      );
    }
    triggerNativeDownload(file.url, resolveFileName(file, index));
    onProgress?.(index + 1, files.length);
  }
  return [];
};

export const saveBlobAsFile = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
